import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Table,
  TableBody,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import CustomBox from 'src/components/StyledComponents/StyledBox';
import { StyledSettingsContainer } from 'src/components/StyledComponents/StyledSettingsTab';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import {
  StyledTableCell,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
} from 'src/components/StyledComponents/StyledTable';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import {
  apiUrls,
  billingBlockedStatuses,
  billingMessages,
  errorMessages,
} from 'src/utils/properties.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

type BillingStep = 'overview' | 'plans' | 'payment';

type SubscriptionStatus = {
  id: string;
  status: string;
  currency: string;
  baseSetupAmount: number | string;
  perUserAmount: number | string;
  paidUserCount: number;
  trialStartsAt?: string;
  trialEndsAt?: string;
  currentPeriodStartsAt?: string;
  currentPeriodEndsAt?: string;
};

type SubscriptionPayment = {
  id: string;
  currency: string;
  totalAmount: number | string;
  status: string;
  paidAt?: string;
  createdAt?: string;
};

type SubscriptionInvoice = {
  id: string;
  invoiceNo: string;
  type: string;
  status: string;
  createdAt?: string;
  payment?: SubscriptionPayment;
  payments?: SubscriptionPayment[];
};

type DownloadError = {
  message?: string;
  response?: {
    data?: Blob | { message?: string; error?: string };
  };
};

const getDownloadErrorMessage = async (error: unknown) => {
  const downloadError = error as DownloadError;
  const data = downloadError.response?.data;

  if (data instanceof Blob) {
    const text = await data.text();

    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || text;
    } catch {
      return text;
    }
  }

  return (
    data?.message ||
    data?.error ||
    downloadError.message ||
    'Failed to download invoice'
  );
};

type BillingTabProps = {
  forcePaymentPrompt?: boolean;
};

export const BillingTab: FC<BillingTabProps> = ({
  forcePaymentPrompt = false,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const showSnackbarRef = useRef(showSnackbar);
  const checkoutMonitorRef = useRef<number | null>(null);
  const hasPendingCheckoutRef = useRef(false);

  useEffect(() => {
    showSnackbarRef.current = showSnackbar;
  }, [showSnackbar]);

  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(
    null
  );
  const [invoices, setInvoices] = useState<SubscriptionInvoice[]>([]);
  const [step, setStep] = useState<BillingStep>('overview');
  const [paidUserCount, setPaidUserCount] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isPayhereCheckoutOpen, setIsPayhereCheckoutOpen] = useState(false);
  const [isCancellingCheckout, setIsCancellingCheckout] = useState(false);
  const [isPaymentPromptOpen, setIsPaymentPromptOpen] =
    useState(forcePaymentPrompt);

  const baseSetupAmount = Number(subscription?.baseSetupAmount ?? 0);
  const perUserAmount = Number(subscription?.perUserAmount ?? 0);
  const currency = subscription?.currency || 'LKR';
  const totalAmount = baseSetupAmount + perUserAmount * paidUserCount;
  const isTrialStillActive = useMemo(() => {
    if (!subscription?.trialEndsAt) return false;

    const trialEndsAt = dayjs(subscription.trialEndsAt);

    return trialEndsAt.isValid() && trialEndsAt.isAfter(dayjs());
  }, [subscription?.trialEndsAt]);
  const isBillingBlocked = useMemo(() => {
    if (!subscription?.status) return false;
    if (subscription.status === 'PENDING_PAYMENT' && isTrialStillActive) {
      return false;
    }

    return billingBlockedStatuses.includes(subscription.status);
  }, [isTrialStillActive, subscription?.status]);
  const shouldShowPaymentPrompt =
    isBillingBlocked || (forcePaymentPrompt && !subscription?.status);
  const isSubscriptionActive = subscription?.status === 'ACTIVE';
  const billingDateLabel = useMemo(() => {
    return subscription?.status === 'TRIAL_ACTIVE'
      ? billingMessages.TRIAL_END_DATE_LABEL
      : billingMessages.NEXT_BILLING_DATE_LABEL;
  }, [subscription?.status]);

  const nextBillingDate = useMemo(() => {
    const dateValue =
      subscription?.status === 'TRIAL_ACTIVE'
        ? subscription?.trialEndsAt
        : subscription?.currentPeriodEndsAt;

    if (!dateValue) {
      return '-';
    }

    const parsedDate = dayjs(dateValue);

    return parsedDate.isValid() ? parsedDate.format('MMM D, YYYY') : '-';
  }, [
    subscription?.status,
    subscription?.trialEndsAt,
    subscription?.currentPeriodEndsAt,
  ]);

  /* */

  const loadBillingData = useCallback(async () => {
    setIsLoading(true);

    try {
      const results = await Promise.allSettled([
        axiosTokenInstance.get(apiUrls.SUBSCRIPTION_STATUS),
        axiosTokenInstance.get(apiUrls.SUBSCRIPTION_INVOICES, {
          params: { offset: 0, limit: 10 },
        }),
      ]);

      const subscriptionRes = results[0];
      const invoicesRes = results[1];

      if (subscriptionRes.status === 'fulfilled') {
        setSubscription(subscriptionRes.value.data?.data || null);
      } else {
        console.error('Subscription API Error:', subscriptionRes.reason);
      }

      if (invoicesRes.status === 'fulfilled') {
        const invoiceData = invoicesRes.value.data?.data;

        setInvoices(
          Array.isArray(invoiceData?.data)
            ? invoiceData.data
            : Array.isArray(invoiceData)
            ? invoiceData
            : []
        );
      } else {
        console.error('Invoices API Error:', invoicesRes.reason);
        setInvoices([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /* */

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  useEffect(() => {
    if (shouldShowPaymentPrompt) {
      setIsPaymentPromptOpen(true);
      return;
    }

    if (subscription?.status) {
      setIsPaymentPromptOpen(false);
    }
  }, [shouldShowPaymentPrompt, subscription?.status]);

  useEffect(() => {
    return () => {
      if (checkoutMonitorRef.current) {
        window.clearInterval(checkoutMonitorRef.current);
      }
    };
  }, []);

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      const response = await axiosTokenInstance.get(
        apiUrls.DOWNLOAD_INVOICE(invoiceId),
        {
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;

      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);

      showSnackbarRef.current(await getDownloadErrorMessage(error), 'error');
    }
  };

  const submitPayhereCheckout = (
    checkoutUrl: string,
    payload: Record<string, unknown>,
    target?: string
  ) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = checkoutUrl;

    if (target) {
      form.target = target;
    }

    Object.entries(payload).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = String(value ?? '');
      form.appendChild(input);
    });

    document.body.appendChild(form);
    form.submit();
    form.remove();
  };

  const showAlreadySubscribedMessage = () => {
    showSnackbarRef.current(
      billingMessages.ALREADY_SUBSCRIBED_MESSAGE,
      'error'
    );
  };

  const cancelPendingCheckout = async (nextStep: BillingStep) => {
    try {
      setIsCancellingCheckout(true);
      await axiosTokenInstance.post(apiUrls.SUBSCRIPTION_CHECKOUT_CANCEL);
      hasPendingCheckoutRef.current = false;
      setIsPayhereCheckoutOpen(false);
      await loadBillingData();
      setStep(nextStep);
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbarRef.current(
        errorMessage || errorMessages.SOMETHING_WENT_WRONG,
        'error'
      );
    } finally {
      setIsCancellingCheckout(false);
    }
  };

  const handleManagePlan = () => {
    if (isSubscriptionActive) {
      showAlreadySubscribedMessage();
      return;
    }

    setStep('plans');
  };

  const createCheckoutSession = async () => {
    const response = await axiosTokenInstance.post(
      apiUrls.SUBSCRIPTION_CHECKOUT,
      {
        paidUserCount,
      }
    );

    const checkoutUrl = response.data?.data?.checkoutUrl;
    const payload = response.data?.data?.checkoutPayload;

    if (!checkoutUrl || !payload) {
      throw new Error('Invalid checkout response');
    }

    await loadBillingData();

    return { checkoutUrl, payload };
  };

  const cancelAbandonedCheckout = async () => {
    try {
      const statusResponse = await axiosTokenInstance.get(
        apiUrls.SUBSCRIPTION_STATUS
      );
      const latestStatus = statusResponse.data?.data?.status;

      if (latestStatus === 'ACTIVE') {
        hasPendingCheckoutRef.current = false;
        setIsPayhereCheckoutOpen(false);
        await loadBillingData();
        return;
      }

      await axiosTokenInstance.post(apiUrls.SUBSCRIPTION_CHECKOUT_CANCEL, {
        reason: 'checkout_window_closed',
      });
      hasPendingCheckoutRef.current = false;
      setIsPayhereCheckoutOpen(false);
      await loadBillingData();
    } catch (e) {
      console.error('Failed to cancel abandoned checkout', e);
    }
  };

  const monitorCheckoutWindow = (checkoutWindow: Window | null) => {
    if (!checkoutWindow) return;

    if (checkoutMonitorRef.current) {
      window.clearInterval(checkoutMonitorRef.current);
    }

    checkoutMonitorRef.current = window.setInterval(() => {
      if (!checkoutWindow.closed) return;

      if (checkoutMonitorRef.current) {
        window.clearInterval(checkoutMonitorRef.current);
        checkoutMonitorRef.current = null;
      }

      window.setTimeout(cancelAbandonedCheckout, 5000);
    }, 1000);
  };

  const handleContinueToPayment = async () => {
    if (isSubscriptionActive) {
      showAlreadySubscribedMessage();
      return;
    }

    try {
      setStep('payment');
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbarRef.current(
        errorMessage || errorMessages.SOMETHING_WENT_WRONG,
        'error'
      );
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  const handleCheckout = async () => {
    if (isCheckoutLoading || hasPendingCheckoutRef.current) {
      return;
    }

    if (isSubscriptionActive) {
      showAlreadySubscribedMessage();
      return;
    }

    let checkoutWindow: Window | null = null;

    try {
      setIsCheckoutLoading(true);
      const checkoutWindowName = `payhere_checkout_${Date.now()}`;
      checkoutWindow = window.open('', checkoutWindowName);
      const session = await createCheckoutSession();

      hasPendingCheckoutRef.current = true;
      setIsPayhereCheckoutOpen(true);
      submitPayhereCheckout(
        session.checkoutUrl,
        session.payload,
        checkoutWindow ? checkoutWindowName : undefined
      );
      monitorCheckoutWindow(checkoutWindow);
    } catch (e) {
      checkoutWindow?.close();
      hasPendingCheckoutRef.current = false;
      setIsPayhereCheckoutOpen(false);
      const errorMessage = await getApiErrorMessage(e);
      showSnackbarRef.current(
        errorMessage || errorMessages.SOMETHING_WENT_WRONG,
        'error'
      );
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  return (
    <StyledSettingsContainer>
      <Dialog
        open={isPaymentPromptOpen && shouldShowPaymentPrompt}
        onClose={() => setIsPaymentPromptOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle fontFamily="Outfit" fontWeight={700}>
          {billingMessages.COMPLETE_SUBSCRIPTION_TITLE}
        </DialogTitle>
        <DialogContent>
          <Typography fontFamily="Outfit" fontSize="14px">
            {billingMessages.BILLING_BLOCKED_DESCRIPTION}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setIsPaymentPromptOpen(false)}
            sx={{ textTransform: 'none' }}
          >
            {billingMessages.REVIEW_BILLING_ACTION}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setIsPaymentPromptOpen(false);
              setStep('plans');
            }}
            sx={{ textTransform: 'none' }}
          >
            {billingMessages.COMPLETE_PAYMENT_ACTION}
          </Button>
        </DialogActions>
      </Dialog>

      {step === 'overview' && (
        <>
          <CustomBox title="Subscription" width="auto">
            <Box display="flex" justifyContent="space-between" gap={2}>
              <Box>
                <Typography
                  fontFamily="Outfit"
                  fontWeight={600}
                  fontSize="18px"
                >
                  Current Plan
                </Typography>
                {isBillingBlocked && (
                  <Typography
                    fontFamily="Outfit"
                    fontSize="14px"
                    color="error"
                    mt={1}
                  >
                    {billingMessages.BILLING_BLOCKED_INLINE_MESSAGE}
                  </Typography>
                )}
                <Typography
                  fontFamily="Outfit"
                  fontSize="14px"
                  color="var(--instructions)"
                >
                  {billingMessages.STATUS_LABEL}: {subscription?.status ?? '-'}
                </Typography>
                <Typography
                  fontFamily="Outfit"
                  fontSize="14px"
                  color="var(--instructions)"
                >
                  {billingDateLabel}: {nextBillingDate}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title={billingMessages.REFRESH_BILLING_DATA}>
                  <span>
                    <IconButton
                      aria-label={billingMessages.REFRESH_BILLING_DATA}
                      onClick={loadBillingData}
                      disabled={isLoading}
                      sx={{
                        border: '1px solid var(--border)',
                        color: 'var(--primary-navy)',
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>

                <Button
                  variant="contained"
                  sx={{ textTransform: 'none', fontFamily: 'Outfit' }}
                  onClick={handleManagePlan}
                  disabled={isLoading}
                >
                  {isBillingBlocked
                    ? billingMessages.COMPLETE_PAYMENT_ACTION
                    : billingMessages.MANAGE_PLAN_ACTION}
                </Button>
              </Box>
            </Box>
          </CustomBox>

          <CustomBox title="Billing History" width="auto">
            <StyledTableContainer>
              <Table>
                <StyledTableHead>
                  <StyledTableRow>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell>Amount</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell align="right">Action</StyledTableCell>
                  </StyledTableRow>
                </StyledTableHead>
                <TableBody>
                  {invoices.length === 0 ? (
                    <StyledTableRow>
                      <StyledTableCell>—</StyledTableCell>
                      <StyledTableCell>—</StyledTableCell>
                      <StyledTableCell>—</StyledTableCell>
                      <StyledTableCell align="right">—</StyledTableCell>
                    </StyledTableRow>
                  ) : (
                    invoices.map((invoice) => {
                      const invoiceDate = invoice.createdAt
                        ? dayjs(invoice.createdAt).format('MMM D, YYYY')
                        : '—';

                      const paidPayment =
                        invoice.payment?.status === 'PAID'
                          ? invoice.payment
                          : (invoice.payments || []).find(
                              (p) => p.status === 'PAID'
                            );
                      const amount =
                        paidPayment?.totalAmount === undefined
                          ? '—'
                          : `${paidPayment.currency || currency} ${Number(
                              paidPayment.totalAmount
                            ).toFixed(2)}`;

                      return (
                        <StyledTableRow key={invoice.id}>
                          <StyledTableCell>{invoiceDate}</StyledTableCell>
                          <StyledTableCell>{amount}</StyledTableCell>
                          <StyledTableCell>
                            <Chip label={invoice.status} size="small" />
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            <Button
                              variant="text"
                              onClick={() => handleDownloadInvoice(invoice.id)}
                            >
                              Download
                            </Button>
                          </StyledTableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </CustomBox>
        </>
      )}

      {step === 'plans' && (
        <CustomBox title="Choose Subscription Plan" width="auto">
          <Button
            onClick={() => cancelPendingCheckout('overview')}
            disabled={isCancellingCheckout}
            sx={{ mb: 2 }}
          >
            {isCancellingCheckout
              ? billingMessages.UPDATING_ACTION
              : billingMessages.BACK_ACTION}
          </Button>

          <Box border="1px solid var(--border)" borderRadius="12px" p={3}>
            <Typography
              fontFamily="Outfit"
              fontWeight={600}
              fontSize="20px"
              mb={1}
            >
              OrderX Subscription
            </Typography>

            <Typography>
              Base setup amount: {currency} {baseSetupAmount.toFixed(2)}
            </Typography>
            <Typography>
              Per user amount: {currency} {perUserAmount.toFixed(2)}
            </Typography>

            <Box mt={3} mb={2}>
              <Typography mb={1}>Paid user count</Typography>
              <TextField
                type="number"
                size="small"
                value={paidUserCount}
                inputProps={{ min: 1 }}
                onChange={(e) =>
                  setPaidUserCount(Math.max(1, Number(e.target.value)))
                }
              />
            </Box>

            <Typography fontFamily="Outfit" fontWeight={600} fontSize="18px">
              Total: {currency} {totalAmount.toFixed(2)}
            </Typography>

            <Button
              variant="contained"
              sx={{ mt: 3, textTransform: 'none' }}
              onClick={handleContinueToPayment}
              disabled={isCheckoutLoading}
            >
              {isCheckoutLoading
                ? billingMessages.UPDATING_ACTION
                : 'Continue to payment'}
            </Button>
          </Box>
        </CustomBox>
      )}

      {step === 'payment' && (
        <CustomBox title="Confirm Payment" width="auto">
          <Button
            onClick={() => cancelPendingCheckout('plans')}
            disabled={isCancellingCheckout}
            sx={{ mb: 2 }}
          >
            {isCancellingCheckout
              ? billingMessages.UPDATING_ACTION
              : billingMessages.BACK_ACTION}
          </Button>

          <Typography>Plan: OrderX Subscription</Typography>
          <Typography>
            Base setup amount: {currency} {baseSetupAmount.toFixed(2)}
          </Typography>
          <Typography>Paid users: {paidUserCount}</Typography>
          <Typography>
            Per user amount: {currency} {perUserAmount.toFixed(2)}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography fontFamily="Outfit" fontWeight={700} fontSize="22px">
            Due today: {currency} {totalAmount.toFixed(2)}
          </Typography>

          <Button
            variant="contained"
            sx={{ mt: 3, textTransform: 'none' }}
            onClick={handleCheckout}
            disabled={isCheckoutLoading || isPayhereCheckoutOpen}
          >
            {isCheckoutLoading || isPayhereCheckoutOpen
              ? 'Redirecting...'
              : 'Pay with PayHere'}
          </Button>
        </CustomBox>
      )}
    </StyledSettingsContainer>
  );
};
